import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useSchemaKey } from '@/composables/schema'
import { useApiStore } from '@/stores/api'

import Payload from '@/lib/Payload'
import { hotReloadStore } from '@/utils/build'

import type { IssueFormSource, IssueSchema, IssueSection } from '@policyfly/schema/types/schemas/issue'
import type { FormSchema } from '@policyfly/schema/types/shared/formSchema'
import type { APIPayloadResponse } from '@policyfly/utils/types'
import type { ComputedRef, Ref } from 'vue'

export interface FormIssueStore {
  file: Ref<IssueSchema | null>
  responses: Ref<APIPayloadResponse[]>
  schema: ComputedRef<FormSchema<IssueFormSource, IssueSection> | null>
  issuePayload: ComputedRef<Payload | null>
  loadSections: () => Promise<IssueSection[] | undefined>
}

export const useFormIssueStore = defineStore('formIssue', (): FormIssueStore => {
  const file: FormIssueStore['file'] = ref(null)
  const responses: FormIssueStore['responses'] = ref([])

  const schema: FormIssueStore['schema'] = computed(() => file.value)
  const issuePayload: FormIssueStore['issuePayload'] = computed(() => {
    if (!responses.value || !responses.value.length) return null
    return Payload.fromResponses(responses.value)
  })

  const apiStore = useApiStore()
  const { getKey, updateKey } = useSchemaKey({ data: file })
  const loadSections: FormIssueStore['loadSections'] = async () => {
    const { key, shouldLoad } = getKey()
    if (!shouldLoad) return file.value?.sections
    const result = await apiStore.schema.issue(key)
    if (result.type.oneofKind !== 'legacy') {
      throw new Error('Unsupported schema type')
    }
    const mod = JSON.parse(result.type.legacy.json) as IssueSchema
    file.value = mod
    updateKey(key)
    return mod.sections
  }

  return {
    file,
    responses,
    schema,
    issuePayload,
    loadSections,
  }
})

hotReloadStore(useFormIssueStore)
